import React from "react";
import "./AboutUs.css";
import {
  FaShieldAlt,
  FaRocket,
  FaUsersCog,
  FaAward,
  FaCalculator,
  FaCity,
  FaChartLine,
  FaCheckCircle,
} from "react-icons/fa";
import useInView from "../hooks/useInView";

const WHY_ITEMS = [
  {
    Icon: FaShieldAlt,
    title: "Quality You Can Trust",
    desc: "Every deliverable passes our internal QA before it reaches you.",
  },
  {
    Icon: FaRocket,
    title: "Built for Speed",
    desc: "Overnight turnaround on revisions, aligned to US business hours.",
  },
  {
    Icon: FaUsersCog,
    title: "Skilled Specialists",
    desc: "Industry-trained team across estimation, design, and real estate.",
  },
  {
    Icon: FaAward,
    title: "Proven Track Record",
    desc: "500+ projects shipped, 50+ long-term client partnerships.",
  },
];

const AboutUs = () => {
  const [aboutRef, aboutInView] = useInView();
  const [whyRef, whyInView] = useInView();

  return (
    <section
      id="about"
      ref={aboutRef}
      className={`about-section ${aboutInView ? "in-view" : ""}`}
    >
      {/* Ambient gradient orbs */}
      <span className="about-orb orb-left" />
      <span className="about-orb orb-right" />

      <div className="about-container">
        <div className="about-text">
          <span className="section-eyebrow">Welcome To Vezron</span>
          <h2>
            Designing, Estimation &amp;{" "}
            <span className="gradient-text">Real Estate</span> Solutions
          </h2>
          <p>
            Vezron Technologies Pvt. Ltd. is a trusted partner for businesses
            that need professional designing and estimation services and
            expert property brokerage. We deliver practical solutions built
            by a team that treats every project as if it were their own.
          </p>
          <p>
            Our work is handled with the precision of industry experts who
            have spent real time on real sites. From cost estimates to
            detailed markups — every deliverable is built to stand up to
            scrutiny.
          </p>

          <div className="about-quick-stats">
            <div className="qs">
              <strong>24/7</strong>
              <span>Overnight Delivery</span>
            </div>
            <div className="qs">
              <strong>99.8%</strong>
              <span>Accuracy Rate</span>
            </div>
          </div>
        </div>

        <div className="about-image-wrap">
          <div className="about-visual">
            {/* Decorative neon corner markers */}
            <span className="frame-corner tl" />
            <span className="frame-corner tr" />
            <span className="frame-corner bl" />
            <span className="frame-corner br" />

            {/* Service card 1 — Designing & Estimation */}
            <div className="av-card av-card-1">
              <div className="av-card-glow" />
              <div className="av-card-icon">
                <FaCalculator />
              </div>
              <h4>Designing &amp; Estimation</h4>
              <ul>
                <li><FaCheckCircle /> Quantity Takeoff</li>
                <li><FaCheckCircle /> Bid Estimation</li>
                <li><FaCheckCircle /> CAD Drafting</li>
              </ul>
              <div className="av-bar"><span style={{ width: "92%" }} /></div>
              <span className="av-bar-label">Accuracy · 99.8%</span>
            </div>

            {/* Service card 2 — Real Estate */}
            <div className="av-card av-card-2">
              <div className="av-card-glow" />
              <div className="av-card-icon">
                <FaCity />
              </div>
              <h4>Real Estate Services</h4>
              <ul>
                <li><FaCheckCircle /> Investment Sales</li>
                <li><FaCheckCircle /> Commercial Leasing</li>
                <li><FaCheckCircle /> Brokerage Advisory</li>
              </ul>
              <div className="av-trend">
                <FaChartLine />
                <span>Growing Portfolio</span>
              </div>
            </div>

            {/* Floating ambient particles */}
            <span className="av-particle p1" />
            <span className="av-particle p2" />
            <span className="av-particle p3" />
          </div>

          <div className="image-badge">
            <strong>5+</strong>
            <span>Years of Excellence</span>
          </div>
        </div>
      </div>

      {/* WHY CHOOSE */}
      <div
        ref={whyRef}
        className={`why-choose ${whyInView ? "in-view" : ""}`}
      >
        <span className="section-eyebrow">Why Vezron</span>
        <h2 className="why-heading">
          Built for outcomes, not <span className="gradient-text">excuses</span>
        </h2>
        <p className="why-text">
          Years of experience across estimation, design, and real estate mean
          we bring trusted expertise, reliable support, and efficient
          execution to every project.
        </p>

        <div className="why-grid">
          {WHY_ITEMS.map(({ Icon, title, desc }, i) => (
            <div
              key={title}
              className="why-card"
              style={{ transitionDelay: `${i * 100}ms` }}
            >
              <div className="why-icon">
                <Icon />
              </div>
              <h3>{title}</h3>
              <p>{desc}</p>
              <span className="why-glow" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default AboutUs;
